// Server: experiments from optimizely-experiments cookie.
// Client: getActiveExperiments() from window.

import type { Request } from 'express';
import { isClient } from '~/modules/utilities/cross_env_utils';

// Cookie carries serialized ExperimentData[] (raw JSON from the Optimizely loader; ).
export const OPTIMIZELY_EXPERIMENTS_COOKIE = 'optimizely-experiments';

export interface ExperimentData {
    experimentId: string;
    variantId: string | null;
}

export interface ExperimentTag {
    experimentId: string;
    variantId: string;
}

export function getExperimentTags(experiments: ExperimentData[]): ExperimentTag[] {
    return experiments.map(({ experimentId, variantId }) => ({
        experimentId,
        variantId: variantId ?? '',
    }));
}

export interface OptimizelyVariation {
    id?: string | number | null;
    name?: string | null;
    index?: number;
}

export interface OptimizelyState {
    getVariationMap?: () => Record<string, OptimizelyVariation>;
}

export interface OptimizelyClient {
    get?: (key: 'state') => OptimizelyState | undefined;
}

export interface WindowWithOptimizely extends Window {
    optimizely?: OptimizelyClient;
}

// Client: ExperimentData[] from optimizely.get('state').getVariationMap() (experiment id -> assigned variation).
export function getActiveExperiments(): ExperimentData[] {
    try {
        if (!isClient) {
            return [];
        }

        // Access window.optimizely directly (browser-only)
        const optimizely = typeof window !== 'undefined' ? (window as WindowWithOptimizely).optimizely : undefined;
        if (!optimizely) {
            return [];
        }

        const variationMap = optimizely.get?.('state')?.getVariationMap?.() ?? {};

        return Object.entries(variationMap).map(([experimentId, variation]) => ({
            experimentId,
            variantId: variation?.id == null ? null : String(variation.id),
        }));
    } catch (error) {
        // Silently fail - we don't want errors in error tracking
        return [];
    }
}

// Parse JSON string into ExperimentData[]
// Format is controlled by optimizely_loader_script.ts which writes:
// [{ experimentId: string, variantId: string | null }, ...]
function parseExperimentData(jsonString: string): ExperimentData[] {
    try {
        const parsed = JSON.parse(jsonString);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

// Server: reads OPTIMIZELY_EXPERIMENTS_COOKIE.
export function getExperimentsFromRequest(req: Partial<Request>): ExperimentData[] {
    try {
        const cookieStr = req.cookies?.[OPTIMIZELY_EXPERIMENTS_COOKIE];
        if (cookieStr) {
            // DoS protection: reject payloads over 10KB
            if (cookieStr.length > 10000) {
                return [];
            }
            // Decode URI-encoded cookies
            const decoded = decodeURIComponent(cookieStr);
            return parseExperimentData(decoded);
        }

        return [];
    } catch {
        return [];
    }
}
